import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Box,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreIcon from '@mui/icons-material/Restore';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DateCapacityIndicator from './DateCapacityIndicator';

interface DayCapacity {
  max_capacity: number;
  total_bookings: number;
  confirmed: number;
  checked_in: number;
  checked_out: number;
  cancelled: number;
  no_show: number;
  active_bookings: number;
  available_spots: number;
  utilization_percent: number;
}

interface DayBooking {
  id: string;
  dependent?: {
    first_name: string;
    last_name: string;
    date_of_birth?: string;
  };
  member?: {
    first_name: string;
    last_name: string;
  };
  start_time: string;
  end_time: string;
  status: string;
  booking_type?: string;
  parent_location?: string;
  cancellation_reason?: string;
  check_in_id?: string;
}

interface TimeSlotConfig {
  enabled: boolean;
  start_time: string;
  end_time: string;
  capacity: number;
}

interface DaySchedule {
  closed: boolean;
  capacity: number;
  time_slots?: {
    morning?: TimeSlotConfig;
    afternoon?: TimeSlotConfig;
    evening?: TimeSlotConfig;
  };
}

interface DailyBookingBreakdownProps {
  date: string;
  dayOfWeek: string;
  isClosed: boolean;
  capacity: DayCapacity;
  bookings?: DayBooking[];
  daySchedule?: DaySchedule;
  defaultExpanded?: boolean;
  onConfirmBooking?: (bookingId: string) => void;
  onCancelBooking?: (bookingId: string) => void;
  onReinstateBooking?: (bookingId: string) => void;
  onCheckIn?: (bookingId: string) => void;
  onCheckOut?: (checkInId: string) => void;
  onAddBooking?: (date: string) => void;
  onEditBooking?: (booking: DayBooking) => void;
}

/**
 * Expandable daily booking breakdown component
 * Shows capacity summary and detailed booking list for a single day
 */
const DailyBookingBreakdown: React.FC<DailyBookingBreakdownProps> = ({
  date,
  dayOfWeek,
  isClosed,
  capacity,
  bookings = [],
  daySchedule,
  defaultExpanded = false,
  onConfirmBooking,
  onCancelBooking,
  onReinstateBooking,
  onCheckIn,
  onCheckOut,
  onAddBooking,
  onEditBooking,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [bookingFilter, setBookingFilter] = useState<'all' | 'pending' | 'waitlisted' | 'pending_checkin' | 'checked_in' | 'checked_out' | 'cancelled'>('all');

  // Filter bookings based on selected filter
  const filterBookings = (bookingsList: DayBooking[]) => {
    switch (bookingFilter) {
      case 'pending':
        return bookingsList.filter(b => b.status === 'pending');
      case 'waitlisted':
        return bookingsList.filter(b => b.status === 'waitlisted');
      case 'pending_checkin':
        return bookingsList.filter(b => b.status === 'confirmed');
      case 'checked_in':
        return bookingsList.filter(b => b.status === 'checked_in');
      case 'checked_out':
        return bookingsList.filter(b => b.status === 'checked_out');
      case 'cancelled':
        return bookingsList.filter(b => b.status === 'cancelled');
      case 'all':
      default:
        return bookingsList;
    }
  };

  // Helper to determine which time slot a booking belongs to
  // Handles edge cases where children are checked in early (before official hours) or late
  const getBookingTimeSlot = (booking: DayBooking): 'morning' | 'afternoon' | 'evening' | 'other' => {
    if (!daySchedule?.time_slots) return 'other';

    const startTime = booking.start_time;

    // First, try exact match within time slot boundaries
    // Check morning (check if time slot exists, not if enabled - so we can show bookings on closed days)
    if (daySchedule.time_slots.morning) {
      const morningStart = daySchedule.time_slots.morning.start_time;
      const morningEnd = daySchedule.time_slots.morning.end_time;
      if (startTime >= morningStart && startTime < morningEnd) {
        return 'morning';
      }
    }

    // Check afternoon
    if (daySchedule.time_slots.afternoon) {
      const afternoonStart = daySchedule.time_slots.afternoon.start_time;
      const afternoonEnd = daySchedule.time_slots.afternoon.end_time;
      if (startTime >= afternoonStart && startTime < afternoonEnd) {
        return 'afternoon';
      }
    }

    // Check evening
    if (daySchedule.time_slots.evening) {
      const eveningStart = daySchedule.time_slots.evening.start_time;
      const eveningEnd = daySchedule.time_slots.evening.end_time;
      if (startTime >= eveningStart && startTime < eveningEnd) {
        return 'evening';
      }
    }

    // If no exact match, find the closest/most appropriate session
    // This handles edge cases like checking in at 8:40 AM when morning starts at 9:00 AM

    // Convert time string to minutes for easier comparison
    const timeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const startMinutes = timeToMinutes(startTime);

    // Build list of available sessions with their start times (check existence, not enabled status)
    const sessions: Array<{ slot: 'morning' | 'afternoon' | 'evening', startMinutes: number, endMinutes: number }> = [];

    if (daySchedule.time_slots.morning) {
      sessions.push({
        slot: 'morning',
        startMinutes: timeToMinutes(daySchedule.time_slots.morning.start_time),
        endMinutes: timeToMinutes(daySchedule.time_slots.morning.end_time),
      });
    }
    if (daySchedule.time_slots.afternoon) {
      sessions.push({
        slot: 'afternoon',
        startMinutes: timeToMinutes(daySchedule.time_slots.afternoon.start_time),
        endMinutes: timeToMinutes(daySchedule.time_slots.afternoon.end_time),
      });
    }
    if (daySchedule.time_slots.evening) {
      sessions.push({
        slot: 'evening',
        startMinutes: timeToMinutes(daySchedule.time_slots.evening.start_time),
        endMinutes: timeToMinutes(daySchedule.time_slots.evening.end_time),
      });
    }

    // If no sessions available, return 'other'
    if (sessions.length === 0) return 'other';

    // Find the closest session
    // Priority: If before first session, assign to first session
    // If after last session, assign to last session
    // If between sessions, assign to the next upcoming session

    // Sort sessions by start time
    sessions.sort((a, b) => a.startMinutes - b.startMinutes);

    // If before the first session start time, assign to first session (handles early arrivals)
    if (startMinutes < sessions[0].startMinutes) {
      return sessions[0].slot;
    }

    // If after the last session end time, assign to last session (handles late arrivals)
    if (startMinutes >= sessions[sessions.length - 1].endMinutes) {
      return sessions[sessions.length - 1].slot;
    }

    // If between sessions, assign to the next upcoming session
    for (let i = 0; i < sessions.length - 1; i++) {
      const currentSession = sessions[i];
      const nextSession = sessions[i + 1];

      if (startMinutes >= currentSession.endMinutes && startMinutes < nextSession.startMinutes) {
        // Between two sessions - assign to the next one
        return nextSession.slot;
      }
    }

    return 'other';
  };

  // Apply filter first, then group bookings by time slot
  const filteredBookings = filterBookings(bookings);
  const groupedBookings = {
    morning: filteredBookings.filter(b => getBookingTimeSlot(b) === 'morning'),
    afternoon: filteredBookings.filter(b => getBookingTimeSlot(b) === 'afternoon'),
    evening: filteredBookings.filter(b => getBookingTimeSlot(b) === 'evening'),
    other: filteredBookings.filter(b => getBookingTimeSlot(b) === 'other'),
  };

  // Convert 24-hour time to 12-hour format
  const formatTime12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (dateString: string) => {
    // Parse date as local time to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'primary';
      case 'waitlisted':
        return 'warning';
      case 'checked_in':
        return 'success';
      case 'checked_out':
        return 'default';
      case 'cancelled':
        return 'error';
      case 'no_show':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Render booking table for a time slot
  const renderBookingTable = (slotBookings: DayBooking[], slotName?: string) => {
    if (slotBookings.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}>
          No bookings for this time slot
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Child Name</TableCell>
              <TableCell>Parent</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Cancellation Reason</TableCell>
              {(onConfirmBooking || onCancelBooking || onReinstateBooking || onCheckIn || onCheckOut || onEditBooking) && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {slotBookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>
                  {booking.dependent ? (
                    `${booking.dependent.first_name} ${booking.dependent.last_name}`
                  ) : (
                    'Unknown'
                  )}
                </TableCell>
                <TableCell>
                  {booking.member ? (
                    `${booking.member.first_name} ${booking.member.last_name}`
                  ) : (
                    'Unknown'
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {formatTime12Hour(booking.start_time)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={booking.status}
                    color={getStatusColor(booking.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{booking.parent_location || '-'}</TableCell>
                <TableCell>
                  {booking.cancellation_reason ? (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {booking.cancellation_reason}
                    </Typography>
                  ) : (
                    '-'
                  )}
                </TableCell>
                {(onConfirmBooking || onCancelBooking || onReinstateBooking || onCheckIn || onCheckOut || onEditBooking) && (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {booking.status.toLowerCase() === 'pending' && onConfirmBooking && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => onConfirmBooking(booking.id)}
                        >
                          Confirm
                        </Button>
                      )}
                      {booking.status.toLowerCase() === 'waitlisted' && onConfirmBooking && (
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => onConfirmBooking(booking.id)}
                        >
                          Confirm from Waitlist
                        </Button>
                      )}
                      {booking.status.toLowerCase() === 'confirmed' && onCheckIn && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<LoginIcon />}
                          onClick={() => onCheckIn(booking.id)}
                        >
                          Check In
                        </Button>
                      )}
                      {booking.status.toLowerCase() === 'checked_in' && booking.check_in_id && onCheckOut && (
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          startIcon={<LogoutIcon />}
                          onClick={() => onCheckOut(booking.check_in_id!)}
                        >
                          Check Out
                        </Button>
                      )}
                      {(booking.status.toLowerCase() === 'pending' || booking.status.toLowerCase() === 'waitlisted' || booking.status.toLowerCase() === 'confirmed') && onEditBooking && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => onEditBooking(booking)}
                        >
                          Edit
                        </Button>
                      )}
                      {(booking.status.toLowerCase() === 'pending' || booking.status.toLowerCase() === 'waitlisted' || booking.status.toLowerCase() === 'confirmed' || booking.status.toLowerCase() === 'checked_in') && onCancelBooking && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => onCancelBooking(booking.id)}
                        >
                          {booking.status.toLowerCase() === 'pending' || booking.status.toLowerCase() === 'waitlisted' ? 'Reject' : 'Cancel'}
                        </Button>
                      )}
                      {booking.status.toLowerCase() === 'cancelled' && onReinstateBooking && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<RestoreIcon />}
                          onClick={() => onReinstateBooking(booking.id)}
                        >
                          Reinstate
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div">
              {dayOfWeek}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(date)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {onAddBooking && !isClosed && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAddBooking(date)}
                sx={{ mr: 1 }}
              >
                Add Booking
              </Button>
            )}
            <DateCapacityIndicator
              maxCapacity={capacity.max_capacity}
              activeBookings={capacity.active_bookings}
              availableSpots={capacity.available_spots}
              isClosed={isClosed}
              size="medium"
            />
            <IconButton
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-label="show more"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {capacity.confirmed}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Confirmed
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {capacity.checked_in}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Checked In
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4">
                {capacity.checked_out}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Checked Out
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={capacity.utilization_percent > 90 ? 'error.main' : 'text.primary'}>
                {capacity.utilization_percent}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Utilization
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Expanded Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />

          {isClosed && bookings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Kids Club is closed on this date
            </Typography>
          ) : bookings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No bookings for this date
            </Typography>
          ) : (
            <Box>
              {/* Warning for closed day with existing bookings */}
              {isClosed && (() => {
                // Check if there are any active bookings that need action
                const activeBookings = bookings.filter(b =>
                  ['pending', 'waitlisted', 'confirmed', 'checked_in'].includes(b.status.toLowerCase())
                );

                if (activeBookings.length > 0) {
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Kids Club is closed on this date, but there are existing bookings that need to be cancelled.
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Please contact the parents and cancel these bookings.
                      </Typography>
                    </Alert>
                  );
                } else {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Kids Club is closed on this date.
                      </Typography>
                    </Alert>
                  );
                }
              })()}

              {/* Filter Buttons */}
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <ToggleButtonGroup
                  value={bookingFilter}
                  exclusive
                  onChange={(e, newFilter) => newFilter && setBookingFilter(newFilter)}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="all">Show All</ToggleButton>
                  <ToggleButton value="pending">Pending</ToggleButton>
                  <ToggleButton value="waitlisted">Waitlisted</ToggleButton>
                  <ToggleButton value="pending_checkin">Pending Check-In</ToggleButton>
                  <ToggleButton value="checked_in">Checked In</ToggleButton>
                  <ToggleButton value="checked_out">Checked Out</ToggleButton>
                  <ToggleButton value="cancelled">Cancelled/Rejected</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Morning Time Slot */}
              {(daySchedule?.time_slots?.morning?.enabled || groupedBookings.morning.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Morning Session
                    {daySchedule?.time_slots?.morning && (
                      <>
                        <Chip
                          label={`${formatTime12Hour(daySchedule.time_slots.morning.start_time)} - ${formatTime12Hour(daySchedule.time_slots.morning.end_time)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${groupedBookings.morning.length}/${daySchedule.time_slots.morning.capacity}`}
                          size="small"
                          color={groupedBookings.morning.length >= daySchedule.time_slots.morning.capacity ? 'error' : 'default'}
                        />
                      </>
                    )}
                  </Typography>
                  {renderBookingTable(groupedBookings.morning, 'Morning')}
                </Box>
              )}

              {/* Afternoon Time Slot */}
              {(daySchedule?.time_slots?.afternoon?.enabled || groupedBookings.afternoon.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Afternoon Session
                    {daySchedule?.time_slots?.afternoon && (
                      <>
                        <Chip
                          label={`${formatTime12Hour(daySchedule.time_slots.afternoon.start_time)} - ${formatTime12Hour(daySchedule.time_slots.afternoon.end_time)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${groupedBookings.afternoon.length}/${daySchedule.time_slots.afternoon.capacity}`}
                          size="small"
                          color={groupedBookings.afternoon.length >= daySchedule.time_slots.afternoon.capacity ? 'error' : 'default'}
                        />
                      </>
                    )}
                  </Typography>
                  {renderBookingTable(groupedBookings.afternoon, 'Afternoon')}
                </Box>
              )}

              {/* Evening Time Slot */}
              {(daySchedule?.time_slots?.evening?.enabled || groupedBookings.evening.length > 0) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Evening Session
                    {daySchedule?.time_slots?.evening && (
                      <>
                        <Chip
                          label={`${formatTime12Hour(daySchedule.time_slots.evening.start_time)} - ${formatTime12Hour(daySchedule.time_slots.evening.end_time)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${groupedBookings.evening.length}/${daySchedule.time_slots.evening.capacity}`}
                          size="small"
                          color={groupedBookings.evening.length >= daySchedule.time_slots.evening.capacity ? 'error' : 'default'}
                        />
                      </>
                    )}
                  </Typography>
                  {renderBookingTable(groupedBookings.evening, 'Evening')}
                </Box>
              )}

              {/* Other/Ungrouped Bookings (if no time slots configured) */}
              {!daySchedule?.time_slots && groupedBookings.other.length > 0 && (
                <Box>
                  {renderBookingTable(groupedBookings.other)}
                </Box>
              )}
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default DailyBookingBreakdown;
